import { Card, Flex, Heading, Text, Image } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import surveysService from './api/services/surveysService';
import { i18n, Loading, parseErrorToString, tavoittajaLogo, useMobileContext } from '@shared';
import { useTranslation } from 'react-i18next';
import SurveyContainer from './views/SurveyContainer';
import { SurveyProvider } from './context/SurveyContext';
import { DateTime } from 'luxon';
import { useEffect } from 'react';

export function Survey() {
	// Extract identifier and token from the path
	const { identifier, token } = useParams<{ identifier: string; token: string }>();

	const { t } = useTranslation();
	const {
		data: survey,
		isLoading,
		error,
	} = useQuery<Survey>({
		queryKey: ['survey', identifier],
		queryFn: () => surveysService.getSurvey(identifier as string, token as string),
		enabled: !!identifier && !!token, // don't run query if missing
	});

	function isSurveyFillableByStartAndEndTime(): boolean {
		if (survey?.surveyStatus === 'disabled') {
			return false;
		}
		const timeSubmitStart = survey?.timeSubmitStart;
		const timeSubmitEnd = survey?.timeSubmitEnd;
		const currentTime = DateTime.utc();

		if (timeSubmitStart === null && timeSubmitEnd === null) {
			return true;
		}

		if (timeSubmitStart && !timeSubmitEnd) {
			const surveyTimeSubmitStart = DateTime.fromFormat(timeSubmitStart as string, 'yyyy-MM-dd HH:mm:ss', { zone: 'UTC' });

			if (surveyTimeSubmitStart > currentTime) {
				return false;
			}

			return true;
		}

		if (!timeSubmitStart && timeSubmitEnd) {
			const surveyTimeSubmitEnd = DateTime.fromFormat(timeSubmitEnd as string, 'yyyy-MM-dd HH:mm:ss', { zone: 'UTC' });

			if (surveyTimeSubmitEnd < currentTime) {
				return false;
			}

			return true;
		}

		if (timeSubmitStart && timeSubmitEnd) {
			const surveyTimeSubmitStart = DateTime.fromFormat(timeSubmitStart as string, 'yyyy-MM-dd HH:mm:ss', { zone: 'UTC' });
			const surveyTimeSubmitEnd = DateTime.fromFormat(timeSubmitEnd as string, 'yyyy-MM-dd HH:mm:ss', { zone: 'UTC' });

			if (currentTime < surveyTimeSubmitStart || currentTime > surveyTimeSubmitEnd) {
				return false;
			}

			return true;
		}

		return false;
	}

	useEffect(() => {
		const surveyLanguages = survey?.languages;
		if (surveyLanguages?.length) {
			const browserLang = navigator.language.split('-')[0];
			if (!browserLang) {
				i18n.changeLanguage(survey?.languages[0]);
				return;
			}
			const languageToSet = surveyLanguages.includes(browserLang as Lang) ? browserLang : surveyLanguages[0];
			i18n.changeLanguage(languageToSet);
		}
	}, [survey?.languages]);

	if (isLoading) return <Loading />;
	if (error || !survey) return <SurveyError error={parseErrorToString(error) || t('general.survey_not_found')} />;
	if (survey.surveyElements?.length === 0)
		return (
			<Flex height="100%" justifyContent={'center'} alignItems={'center'}>
				<Heading textAlign={'center'}>{t('general.survey_not_fillable')}</Heading>
			</Flex>
		);
	if (isSurveyFillableByStartAndEndTime() === false) {
		return (
			<Flex height="100%" justifyContent={'center'} alignItems={'center'}>
				<Heading textAlign={'center'}>{t('general.survey_not_fillable_time')}</Heading>
			</Flex>
		);
	}

	return (
		<Flex width="100%" height="100%" justifyContent="center" backgroundColor="rgba(251, 249, 255, 1)">
			<style dangerouslySetInnerHTML={{ __html: survey.customCss }} />
			<SurveyProvider defaultSurvey={survey}>
				<SurveyContainer survey={survey} />
			</SurveyProvider>
		</Flex>
	);
}

function SurveyError({ error, isMakeSureLinkIsCorrectShown = false }: { error: string; isMakeSureLinkIsCorrectShown?: boolean }) {
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	return (
		<Flex height="100%" flexDirection="column" justifyContent="center" alignItems="center" padding={2} backgroundColor={'rgba(251, 249, 255, 1)'}>
			<Card.Root borderRadius={'12px'}>
				<Card.Body padding={0} overflow="hidden" backgroundColor="white" borderRadius={'12px'}>
					<Flex>
						{!isMobile ? (
							<Flex backgroundColor="rgb(61, 213, 152)" flexDirection={'column'} alignItems={'center'} justifyContent={'center'} padding={4}>
								<Image height={'60%'} objectFit="contain" padding={2} paddingLeft={7} paddingRight={7} src={tavoittajaLogo}></Image>
								<Heading marginBottom={2} color="white">
									Tavoittaja
								</Heading>
							</Flex>
						) : null}
						<Flex alignItems={'center'} gap={4} padding={4} paddingLeft={10} paddingRight={10} minWidth={'70%'} maxW={'100vw'}>
							<Flex flexDirection="column" maxW={'100%'}>
								<Heading marginBottom={4}>{t('general.oops_something_went_wrong')}</Heading>
								<Text>{error || t('general.unexpected_error')}</Text>
								{isMakeSureLinkIsCorrectShown ? <Text marginTop={2}>{t('general.make_sure_link_is_correct')}</Text> : null}
							</Flex>
						</Flex>
					</Flex>
				</Card.Body>
			</Card.Root>
		</Flex>
	);
}
